import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { App } from 'antd';
import { authApi } from '../api/auth.api';
import {
  clearToken,
  getToken,
  setForbiddenHandler,
  setToken,
  setUnauthorizedHandler,
} from '../api/client';
import type { MeResponse } from '../types/auth';

export type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated';

interface AuthContextValue {
  user: MeResponse | null;
  status: AuthStatus;
  login: (email: string, password: string) => Promise<MeResponse>;
  logout: () => Promise<void>;
  /** True if the current user's role is in the given list (client-side UX gate only). */
  hasRole: (roles: readonly string[]) => boolean;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<MeResponse | null>(null);
  const [status, setStatus] = useState<AuthStatus>('loading');
  const navigate = useNavigate();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const identityRevision = useRef(0);

  const resetIdentity = useCallback((nextStatus: 'loading' | 'unauthenticated' = 'unauthenticated') => {
    identityRevision.current += 1;
    clearToken();
    setUser(null);
    setStatus(nextStatus);
    // clear() also destroys/cancels active queries, including fetches that ignore AbortSignal.
    queryClient.clear();
  }, [queryClient]);

  // Bootstrap: if a token is present, resolve the current identity; otherwise we're logged out.
  useEffect(() => {
    const token = getToken();
    if (!token) {
      resetIdentity();
      return;
    }
    const revision = identityRevision.current;
    queryClient.clear();
    let cancelled = false;
    authApi
      .me()
      .then((me) => {
        if (cancelled || revision !== identityRevision.current || getToken() !== token) return;
        setUser(me);
        setStatus('authenticated');
      })
      .catch(() => {
        if (cancelled || revision !== identityRevision.current || getToken() !== token) return;
        resetIdentity();
      });
    return () => {
      cancelled = true;
    };
  }, [queryClient, resetIdentity]);

  // Wire the Axios 401/403 handlers to router navigation (interceptors live outside React).
  useEffect(() => {
    setUnauthorizedHandler(() => {
      resetIdentity();
      message.warning('نشست شما منقضی شده است. لطفاً دوباره وارد شوید.');
      navigate('/login', { replace: true });
    });
    setForbiddenHandler(() => {
      navigate('/403', { replace: true });
    });
    return () => {
      setUnauthorizedHandler(null);
      setForbiddenHandler(null);
    };
  }, [navigate, message, resetIdentity]);

  const login = useCallback(async (email: string, password: string) => {
    resetIdentity('loading');
    const revision = identityRevision.current;
    try {
      const res = await authApi.login({ email, password });
      if (revision !== identityRevision.current) throw new Error('Authentication attempt was superseded.');
      setToken(res.token);
      const me = await authApi.me();
      if (revision !== identityRevision.current || getToken() !== res.token) {
        throw new Error('Authentication attempt was superseded.');
      }
      queryClient.clear();
      setUser(me);
      setStatus('authenticated');
      return me;
    } catch (error) {
      if (revision === identityRevision.current) resetIdentity();
      throw error;
    }
  }, [queryClient, resetIdentity]);

  const logout = useCallback(async () => {
    // Dispatch with the old bearer first; local logout must not wait for the server response.
    const request = getToken() ? authApi.logout() : Promise.resolve();
    resetIdentity();
    navigate('/login', { replace: true });
    try {
      await request;
    } catch {
      // Best-effort server invalidation; a late response must not alter a subsequent login.
    }
  }, [navigate, resetIdentity]);

  const hasRole = useCallback(
    (roles: readonly string[]) => (user ? roles.includes(user.role) : false),
    [user],
  );

  const value = useMemo<AuthContextValue>(
    () => ({ user, status, login, logout, hasRole }),
    [user, status, login, logout, hasRole],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}
